import { useEffect, useRef, useState } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import { Screen, Panel, PrimaryButton, HeroLens } from '../components/ui.jsx'
import { QrScan, ArrowRight } from '../components/icons.jsx'
import { TENANT as DEMO_TENANT } from '../data/fixtures.js'
import { parseTenantPayload } from '../qrPayload.js'
import './join.css'

// Shown only in demo mode; sourced from the fixture mapping so there is
// exactly one place the demo code is defined.
const DEMO_COMPANY_CODE = DEMO_TENANT.code

/*
 * Join a Team — "Arrival, Not Authentication". The QR encodes a TENANT ID ONLY
 * (nothing else). Two ways in: real device-camera scan, or type the company
 * code. Both resolve the same tenant via the adapter; identity comes next.
 */
const READER_ID = 'flow-qr-reader'

export default function JoinTeam({ adapter, onTenant, onBack, isDemo = false }) {
  const [code, setCode] = useState('')
  const [scanning, setScanning] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const scannerRef = useRef(null)

  async function resolve(payload) {
    setBusy(true); setError('')
    try {
      const tenant = await adapter.resolveTenant(payload)
      onTenant(tenant)
    } catch (e) {
      setError(e.code === 'TENANT_NOT_FOUND' || /404|not found/i.test(e.message)
        ? "We couldn't find that code. Check it and try again."
        : 'Something went wrong reaching your team. Try again.')
      setBusy(false)
    }
  }

  // Camera lifecycle — start when scanning, always stop on exit.
  useEffect(() => {
    if (!scanning) return
    let stopped = false
    const scanner = new Html5Qrcode(READER_ID, { verbose: false })
    scannerRef.current = scanner
    scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 224, height: 224 } },
        (decodedText) => {
          if (stopped) return
          const payload = parseTenantPayload(decodedText)
          if (!payload) return // not a Flow code — keep scanning
          stopped = true
          stopScanner().then(() => resolve(payload))
        },
        () => { /* per-frame decode misses — ignore */ },
      )
      .catch(() => {
        setError('Camera unavailable or permission denied. Enter your company code instead.')
        setScanning(false)
      })
    return () => { stopped = true; stopScanner() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning])

  async function stopScanner() {
    const s = scannerRef.current
    scannerRef.current = null
    if (!s) return
    try { await s.stop() } catch { /* already stopped */ }
    try { await s.clear() } catch { /* noop */ }
  }

  return (
    <Screen>
      <button className="link-back" onClick={onBack}>← Back</button>

      <div className="stack" style={{ marginTop: 'var(--sp-2)' }}>
        <h1 className="title-xl rise" style={{ animationDelay: '60ms' }}>Join a team</h1>
        <p className="subtle rise" style={{ animationDelay: '150ms' }}>
          Scan the code on your trailer or gang box — or enter your company code.
        </p>
      </div>

      <div className="join-body">
        {scanning ? (
          <Panel className="scan-panel rise">
            <div id={READER_ID} className="scan-viewport" />
            <button className="link-back" onClick={() => setScanning(false)}>Cancel scan</button>
          </Panel>
        ) : (
          <Panel as="button" className="scan-cta rise" style={{ animationDelay: '260ms' }}
                 onClick={() => { setError(''); setScanning(true) }} disabled={busy}>
            <HeroLens size={56}><QrScan size={24} /></HeroLens>
            <span className="entry-card__text">
              <span className="entry-card__title">Scan QR code</span>
              <span className="entry-card__sub">Point your camera at the printed code</span>
            </span>
          </Panel>
        )}

        <div className="or-divider" aria-hidden="true"><span>or</span></div>

        <form className="code-row" onSubmit={(e) => { e.preventDefault(); if (code.trim()) resolve(code.trim()) }}>
          <input
            className="code-input"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            placeholder="Company code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            aria-label="Company code"
          />
          <PrimaryButton type="submit" disabled={!code.trim() || busy} aria-label="Continue">
            <ArrowRight size={22} />
          </PrimaryButton>
        </form>

        {error && <p className="field-error rise" role="alert">{error}</p>}

        {/* Demo-only helper — disappears automatically once live data is
            active (isDemo comes from the resolved adapter, not a build flag).
            The code is a convenience label, not a credential. */}
        {isDemo && (
          <p className="meta demo-code-hint rise" style={{ animationDelay: '360ms' }}>
            Demo company code: <strong>{DEMO_COMPANY_CODE}</strong>
          </p>
        )}
      </div>
    </Screen>
  )
}
